import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_PALETTE, isPalette } from '../config/appearance';
import { DEFAULT_ROLE, SEAT_KEYS } from '../config/navigation';

/**
 * Shell state for the PMS app: the login role being viewed, theme + accent
 * palette, the "as of" date every analytics call is dated against, and the
 * transient overlays (time-series modal, copilot drawer, toast).
 *
 * Theme and palette are persisted to localStorage and reflected onto
 * <html data-theme data-palette>, which is exactly how the design's CSS
 * selects its light/dark and accent variants.
 */

const read = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * The copilot panel's width, dragged by its own edge and remembered.
 *
 * Bounded rather than free: below the floor the transcript stops being
 * readable, and above the ceiling the screen the panel is meant to sit beside
 * has nothing left. The stored value is clamped on the way in too, so a hand
 * -edited localStorage cannot open the app with no room to work in.
 */
export const CHAT_MIN = 300;
export const CHAT_MAX = 720;
export const DEFAULT_CHAT_WIDTH = 380;
export const clampChatWidth = (px) => {
  const n = Math.round(Number(px));
  if (!Number.isFinite(n)) return DEFAULT_CHAT_WIDTH;
  return Math.min(CHAT_MAX, Math.max(CHAT_MIN, n));
};

/** A retired palette name is treated as unset rather than left in the picker. */
const storedPalette = read('ki.palette', DEFAULT_PALETTE);

const initialState = {
  /* A seat this build does not have (one saved by another branch of the app
     in the same browser) falls back to the default rather than crashing every
     screen that reads PROFILES[role]. */
  role: (() => { const saved = read('ki.role', DEFAULT_ROLE); return SEAT_KEYS.includes(saved) ? saved : DEFAULT_ROLE; })(),
  /**
   * The role the signed-in account holds. Null means no role is known, which is
   * the design's test-profile mode where every seat can be previewed. Non-null
   * pins the app to that seat.
   */
  assignedRole: null,
  theme: read('ki.theme', 'light'),
  palette: isPalette(storedPalette) ? storedPalette : DEFAULT_PALETTE,
  /* Always today. The header's "As of" picker was the only thing that ever
     moved this, and it is gone — so a date stored by it is no longer read,
     or a past pick would pin every analytics call to that day with no control
     left to undo it. */
  asOf: todayISO(),
  /**
   * The fund the desk is working on, remembered across screens.
   *
   * The deep-dive carries it in the route and Fund Analysis in a query
   * parameter, so moving between the two used to drop it and ask for the fund
   * again. A URL that names a subject still wins; this is only what the screens
   * fall back to.
   */
  subject: read('ki.subject', null) || null,
  /**
   * The client the desk is working on, remembered across screens.
   *
   * The same problem `subject` solves for funds: a client is chosen on the deep
   * dive, and every other screen that works client by client — goals, the
   * planner, proposals — used to open on nobody and ask again. A route or query
   * parameter that names a client still wins; this is only the fallback.
   */
  client: read('ki.client', null) || null,
  /**
   * The fund manager whose book the desk is reading, remembered across screens.
   *
   * My Funds picks one; the fund deep-dive narrows its scheme picker to that
   * person's funds. Without this the picker offered the whole master — 1,400
   * schemes, mostly nothing to do with the manager just chosen.
   */
  manager: read('ki.manager', null) || null,
  /**
   * What the desk has opened lately — schemes, models, stocks.
   *
   * Kept here rather than asked of the backend because nothing records it
   * there: it is this browser's own trail. Stored as a capped, de-duplicated
   * list so revisiting a fund moves it to the front instead of filling the
   * list with the same name.
   */
  recent: (() => {
    try {
      const raw = JSON.parse(read('ki.recent', '[]'));
      return Array.isArray(raw) ? raw.slice(0, 12) : [];
    } catch {
      /* A hand-edited or half-written entry is not worth failing a boot over. */
      return [];
    }
  })(),
  sidebarOpen: false,
  /** Desktop: the sidebar narrowed to icons. Remembered, unlike `sidebarOpen`. */
  sidebarMini: read('ki.sidebarMini') === 'true',
  chatOpen: false,
  chatWidth: clampChatWidth(read('ki.chatWidth', DEFAULT_CHAT_WIDTH)),
  drawerOpen: false,
  /** Universal time-series modal — any rendered number can open its own history. */
  timeSeries: null,
  toast: null,
};

const persist = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / storage disabled — the UI still works, it just won't remember */
  }
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setRole(state, { payload }) {
      /* An account with a role cannot preview another one. */
      if (state.assignedRole && payload !== state.assignedRole) return;
      state.role = payload;
      persist('ki.role', payload);
    },
    /** Set once the session resolves; also forces the view onto that role. */
    setAssignedRole(state, { payload }) {
      state.assignedRole = payload || null;
      if (payload) {
        state.role = payload;
        persist('ki.role', payload);
      }
    },
    setTheme(state, { payload }) {
      state.theme = payload;
      persist('ki.theme', payload);
    },
    toggleTheme(state) {
      state.theme = state.theme === 'dark' ? 'light' : 'dark';
      persist('ki.theme', state.theme);
    },
    setPalette(state, { payload }) {
      state.palette = payload;
      persist('ki.palette', payload);
    },
    /** Remember the fund a screen was opened on. */
    setSubject(state, { payload }) {
      const next = payload || null;
      if (state.subject === next) return;
      state.subject = next;
      if (next) persist('ki.subject', next);
    },
    /**
     * Note that something was opened. Payload: `{ kind, id, label, sub }`.
     *
     * Most recent first, one entry per thing, twelve kept. A visit with no id
     * is ignored rather than stored as a row that cannot be opened again.
     */
    noteViewed(state, { payload }) {
      const { kind, id, label, sub = null } = payload || {};
      if (!kind || !id) return;
      const key = `${kind}:${id}`;
      const next = [
        { kind, id: String(id), label: label || String(id), sub, at: Date.now() },
        ...state.recent.filter((r) => `${r.kind}:${r.id}` !== key),
      ].slice(0, 12);
      state.recent = next;
      persist('ki.recent', JSON.stringify(next));
    },
    /** Remember the fund manager whose book is being read. Null clears it. */
    setManager(state, { payload }) {
      const next = payload || null;
      if (state.manager === next) return;
      state.manager = next;
      /* Cleared as well as set: "every manager" is a real choice, and a stored
         name would quietly re-narrow the next screen that reads this. */
      persist('ki.manager', next || '');
    },
    /** Remember the client a screen was opened on. */
    setClient(state, { payload }) {
      const next = payload ? String(payload) : null;
      if (state.client === next) return;
      state.client = next;
      if (next) persist('ki.client', next);
    },
    setAsOf(state, { payload }) {
      state.asOf = payload;
      persist('ki.asOf', payload);
    },
    setSidebarOpen(state, { payload }) {
      state.sidebarOpen = payload;
    },
    toggleSidebarMini(state) {
      state.sidebarMini = !state.sidebarMini;
      persist('ki.sidebarMini', String(state.sidebarMini));
    },
    setChatOpen(state, { payload }) {
      state.chatOpen = payload;
    },
    /** Dispatched once the drag ends — the drag itself moves the CSS variable. */
    setChatWidth(state, { payload }) {
      state.chatWidth = clampChatWidth(payload);
      persist('ki.chatWidth', String(state.chatWidth));
    },
    setDrawerOpen(state, { payload }) {
      state.drawerOpen = payload;
    },
    /** payload: { label, raw, metricKey, type, entity, holding, scenario } */
    openTimeSeries(state, { payload }) {
      state.timeSeries = payload;
    },
    closeTimeSeries(state) {
      state.timeSeries = null;
    },
    showToast(state, { payload }) {
      state.toast = payload;
    },
    hideToast(state) {
      state.toast = null;
    },
  },
});

export const {
  setRole, setAssignedRole, setTheme, toggleTheme, setPalette, setAsOf,
  setSidebarOpen, toggleSidebarMini, setChatOpen, setChatWidth, setDrawerOpen,
  openTimeSeries, closeTimeSeries, showToast, hideToast, setSubject, setClient, setManager,
  noteViewed,
} = uiSlice.actions;

export default uiSlice.reducer;
